/**
 * DriveRestructurePanel - Panneau Parametres pour restructurer les dossiers Drive.
 *
 * Restructure les dossiers existants depuis l'ancienne arborescence
 * 'Client - X / Projets / Ville Client' vers le nouveau format 'VILLE - NOMS' direct
 * sous le root Drive. Procede en 2 etapes : preview (dry-run) puis execute confirme.
 *
 * Reserve aux CEO / Admin.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DriveRestructurePanel = () => {
  const [plan, setPlan] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({}); // {project_id: true}
  const [error, setError] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.get(`${API}/admin/drive/restructure/preview`);
      setPlan(r.data);
      // Selectionne tout par defaut
      const init = {};
      (r.data.items || []).forEach((it) => { init[it.project_id] = true; });
      setSelected(init);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoadingPreview(false); }
  }, []);

  const toggleAll = (val) => {
    if (!plan?.items) return;
    const next = {};
    plan.items.forEach((it) => { next[it.project_id] = val; });
    setSelected(next);
  };

  const execute = async () => {
    if (!plan?.items?.length) return;
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) {
      alert('Selectionnez au moins un projet a restructurer.');
      return;
    }
    if (!window.confirm(
      `Vous etes sur le point de RENOMMER et DEPLACER ${ids.length} dossier(s) Drive.\n\nCette operation est IRREVERSIBLE.\n\nConfirmer ?`
    )) return;

    setExecuting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/admin/drive/restructure/execute`, {
        project_ids: ids,
      });
      setResult(r.data);
      // Refresh preview
      setTimeout(() => loadPreview(), 800);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setExecuting(false); }
  };

  return (
    <div className="realease-card p-6" data-testid="drive-restructure-panel">
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
          Restructurer les dossiers Drive
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Migre les anciens dossiers <code className="bg-gray-100 px-1 rounded">Client - X / Projets / Ville Client</code> vers le nouveau format <code className="bg-gray-100 px-1 rounded">VILLE - NOMS</code> directement sous le root Drive.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={loadPreview}
          disabled={loadingPreview}
          className="px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
          data-testid="drive-restructure-preview-btn"
        >
          {loadingPreview ? 'Analyse...' : 'Analyser les dossiers a restructurer'}
        </button>
        {plan && plan.items && plan.items.length > 0 && (
          <>
            <button
              type="button"
              onClick={execute}
              disabled={executing}
              className="px-4 py-2 rounded-md text-sm font-semibold bg-[#A57F4F] text-white hover:bg-[#8d6a40] disabled:opacity-50 transition"
              data-testid="drive-restructure-execute-btn"
            >
              {executing ? 'Restructuration en cours...' : `Restructurer ${Object.values(selected).filter(Boolean).length} dossier(s)`}
            </button>
            <button
              type="button"
              onClick={() => toggleAll(true)}
              className="text-xs text-[#13736D] hover:underline"
              data-testid="drive-restructure-select-all"
            >
              Tout selectionner
            </button>
            <button
              type="button"
              onClick={() => toggleAll(false)}
              className="text-xs text-gray-500 hover:underline"
              data-testid="drive-restructure-deselect-all"
            >
              Tout deselectionner
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="drive-restructure-error">
          Erreur : {error}
        </div>
      )}

      {plan && (
        <div className="mb-4">
          {plan.count === 0 ? (
            <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-700" data-testid="drive-restructure-empty">
              Tous les dossiers Drive sont deja au bon format. Rien a faire !
            </div>
          ) : (
            <div data-testid="drive-restructure-plan">
              <p className="text-sm text-gray-600 mb-2">
                <b>{plan.count} dossier(s)</b> a restructurer :
              </p>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 text-left w-6"></th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Projet</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Nom actuel</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Nouveau nom (editable)</th>
                      <th className="px-2 py-2 text-center font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.items.map((it) => (
                      <tr key={it.project_id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!selected[it.project_id]}
                            onChange={(e) => setSelected({ ...selected, [it.project_id]: e.target.checked })}
                            data-testid={`drive-restructure-check-${it.project_id}`}
                          />
                        </td>
                        <td className="px-2 py-2 font-medium text-gray-900">
                          {it.project_name || it.project_id}
                          {it.client_name && (
                            <div className="text-[10px] text-gray-400 font-normal">{it.client_name} · {it.geographic_zone || '—'}</div>
                          )}
                        </td>
                        <td className="px-2 py-2 text-gray-600 font-mono break-all">{it.current_name}</td>
                        <td className="px-2 py-2">
                          <input
                            type="text"
                            defaultValue={it.target_name}
                            onBlur={async (e) => {
                              const v = e.target.value.trim();
                              if (v === it.target_name) return;
                              try {
                                await axios.put(`${API}/admin/drive/folder-override/${it.project_id}`, { name: v });
                                // Met a jour le plan local
                                setPlan((p) => ({
                                  ...p,
                                  items: p.items.map((x) => x.project_id === it.project_id ? { ...x, target_name: v, drive_folder_name_override: v } : x),
                                }));
                              } catch (err) {
                                alert(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
                              }
                            }}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs font-mono text-[#13736D] focus:border-[#13736D] focus:outline-none"
                            data-testid={`drive-restructure-name-${it.project_id}`}
                          />
                          {it.drive_folder_name_override && (
                            <span className="inline-block mt-0.5 text-[9px] px-1 py-0 rounded bg-purple-100 text-purple-700 font-semibold">OVERRIDE MANUEL</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {it.needs_rename && (
                            <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold mr-1">RENOM.</span>
                          )}
                          {it.needs_move && (
                            <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">DEPLAC.</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200" data-testid="drive-restructure-result">
          <p className="font-semibold text-emerald-800 mb-2">
            Restructuration terminee - {result.processed} dossier(s) traite(s).
          </p>
          <ul className="text-xs text-emerald-700 space-y-0.5">
            {(result.results || []).map((r) => (
              <li key={r.project_id}>
                {r.ok ? '✓' : '✗'} {r.project_id}{r.new_name ? ` -> ${r.new_name}` : ''}{r.error ? ` (${r.error})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DriveRestructurePanel;
